import {
  useLazyListPartiesQuery,
  useLazyListPartyTypesQuery
} from './partyApi';
import {
  useLazyListFeaturesQuery,
  useLazyListFeatureCategoriesQuery
} from './featureApi';
import { useLazyListDataZonesQuery } from './datazoneApi';
import { useLazyListGroupsQuery } from './groupApi';
import { useLazyListIndustriesQuery } from './industryApi';
import { useLazyListJobTitlesQuery } from './jobTitleApi';

import {
  useLazyListEntitiesQuery,
  useLazyListEntityTypesQuery,
  useLazyListEntityRolesQuery
} from './entityApi';
import {
  useLazyListContractsQuery,
  useLazyListContractRolesQuery
} from './contractApi';
import {
  useLazyListServicesQuery,
  useLazyListServiceCategoriesQuery,
  useLazyListServiceTypesQuery
} from './serviceApi';

import {
  useLazyListAssetTypesQuery,
  useLazyListBuildingsQuery
} from './assetApi';

import { useLazyListAssetsQuery } from './assetApi';
import { ArtificialDetailPropertyNames } from '../../common/globalNotary';

const AUXILIARY_LIST_PAGE_SIZE = 100;

export const fetchAllPages = async (fetchPage) => {
  const fetchParameters = {
    sort: '+name',
    page: 0,
    per_page: AUXILIARY_LIST_PAGE_SIZE
  };
  const firstResult = await fetchPage(fetchParameters);
  const firstPage = firstResult?.data?.data ?? [];
  const pagination = firstResult?.data?.pagination;
  const pageSize = Number(pagination?.per_page) || AUXILIARY_LIST_PAGE_SIZE;
  const total = Number(pagination?.total) || firstPage.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (firstResult?.error || totalPages === 1) {
    return { data: firstPage, error: firstResult?.error ?? null };
  }

  const remainingResults = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      fetchPage({
        ...fetchParameters,
        page: index + 1
      })
    )
  );
  const error = remainingResults.find((result) => result?.error)?.error ?? null;
  const data = remainingResults.reduce(
    (allEntities, result) => allEntities.concat(result?.data?.data ?? []),
    firstPage
  );

  return { data, error };
};

const useFetchAuxiliaryLists = () => {
  const [getGroups] = useLazyListGroupsQuery();
  const [getFeatures] = useLazyListFeaturesQuery();
  const [getAllFeatureCategories] = useLazyListFeatureCategoriesQuery();
  const [getDataZones] = useLazyListDataZonesQuery();
  const [getParties] = useLazyListPartiesQuery();
  const [getPartyTypes] = useLazyListPartyTypesQuery();
  const [getAllIndustries] = useLazyListIndustriesQuery();
  const [getEntities] = useLazyListEntitiesQuery();
  const [getEntityTypes] = useLazyListEntityTypesQuery();
  const [getEntityRoles] = useLazyListEntityRolesQuery();
  const [getContracts] = useLazyListContractsQuery();
  const [getServices] = useLazyListServicesQuery();
  const [getAssets] = useLazyListAssetsQuery();
  const [getBuildings] = useLazyListBuildingsQuery();
  const [getServiceTypes] = useLazyListServiceTypesQuery();
  const [getServiceCategories] = useLazyListServiceCategoriesQuery();
  const [getContractRoles] = useLazyListContractRolesQuery();
  const [getAssetTypes] = useLazyListAssetTypesQuery();
  const [getJobTitles] = useLazyListJobTitlesQuery();
  const checkArguments = (
    primaryDetailRecord,
    listPropertyName,
    extractionPropertyName
  ) => {
    // throw errors right away if arguments are no good (at construction time, not call time, the latter would be much harder to deubg)
    if (
      !primaryDetailRecord.hasOwnProperty(listPropertyName) &&
      !Object.values(ArtificialDetailPropertyNames).includes(listPropertyName)
    ) {
      throw new Error(`invalid argument: ${listPropertyName}`);
    }
    const ids = primaryDetailRecord[listPropertyName];
    if (!Array.isArray(ids)) throw new Error('invalid argument');

    if (ids.length === 0) {
      const cannedEmptyReturnCall = async () => {
        const emptyList = [];
        emptyList.listPropertyName = listPropertyName;
        return { data: emptyList, error: null };
      };
      return { fetchRequired: false, callBack: cannedEmptyReturnCall };
    }

    if (extractionPropertyName && typeof ids[0] === 'object') {
      return {
        fetchRequired: true,
        ids: ids.map((o) => o[extractionPropertyName])
      };
    } else {
      return { fetchRequired: true, ids };
    }
  };

  const constructValueAsPromiseCall = (
    listPropertyName,
    returnValue,
    compensationFieldArray,
    equalityEvaluator
  ) => {
    return async () => {
      const copy = returnValue ? JSON.parse(JSON.stringify(returnValue)) : [];

      copy.listPropertyName = listPropertyName;

      if (compensationFieldArray && equalityEvaluator) {
        copy.compensationFieldArray = compensationFieldArray;
        copy.equalityEvaluator = equalityEvaluator;
      }

      return { data: copy, error: null };
    };
  };
  const constructTerritoryGroupsByDatazoneIdCall = (
    primaryDetailRecord,
    listPropertyName
  ) => {
    const datazone_id = primaryDetailRecord.id;
    return async () => {
      if (!datazone_id) {
        const emptyArray = [];
        emptyArray.listPropertyName = listPropertyName;
        return { data: emptyArray, error: null };
      }

      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getGroups({ datazone_id });

      const returnValue = JSON.parse(JSON.stringify(data));
      returnValue.listPropertyName = listPropertyName;
      return { data: returnValue, error };
    };
  };

  const constructIndustriesByIdArrayCall = (
    primaryDetailRecord,
    listPropertyName
  ) => {
    const { fetchRequired, ids, callBack } = checkArguments(
      primaryDetailRecord,
      listPropertyName
    );
    if (!fetchRequired) return callBack;
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getAllIndustries();

      const returnValue = JSON.parse(
        JSON.stringify(data.filter((i) => ids.includes(i.id)))
      );
      returnValue.listPropertyName = listPropertyName;
      return { data: returnValue, error };
    };
  };

  const constructEntitiesByIdArrayCall = (
    primaryDetailRecord,
    listPropertyName
  ) => {
    const { fetchRequired, ids, callBack } = checkArguments(
      primaryDetailRecord,
      listPropertyName
    );
    if (!fetchRequired) return callBack;
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getEntities({ ids });

      const returnValue = JSON.parse(JSON.stringify(data));
      returnValue.listPropertyName = listPropertyName;
      return { data: returnValue, error };
    };
  };

  const constructGetAllAssetTypesCall = (
    primaryDetailRecord,
    listPropertyName
  ) => {
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getAssetTypes();

      const returnValue = JSON.parse(JSON.stringify(data));
      returnValue.listPropertyName = listPropertyName;
      return { data: returnValue, error };
    };
  };

  const constructGetAllJobTitlesCall = (
    primaryDetailRecord,
    listPropertyName
  ) => {
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getJobTitles();

      const returnValue = JSON.parse(JSON.stringify(data));
      returnValue.listPropertyName = listPropertyName;
      return { data: returnValue, error };
    };
  };

  //

  const constructGetAllEntitiesCall = (
    primaryDetailRecord,
    listPropertyName
  ) => {
    return async () => {
      const { data, error } = await fetchAllPages(getEntities);

      const returnValue = JSON.parse(JSON.stringify(data));
      returnValue.listPropertyName = listPropertyName;
      return { data: returnValue, error };
    };
  };

  const constructContractsByIdArrayCall = (
    primaryDetailRecord,
    listPropertyName
  ) => {
    const { fetchRequired, ids, callBack } = checkArguments(
      primaryDetailRecord,
      listPropertyName
    );
    if (!fetchRequired) return callBack;
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getContracts({ ids });

      const returnValue = JSON.parse(JSON.stringify(data));
      returnValue.listPropertyName = listPropertyName;
      return { data: returnValue, error };
    };
  };

  const constructAssetsByIdArrayCall = (
    primaryDetailRecord,
    listPropertyName
  ) => {
    const { fetchRequired, ids, callBack } = checkArguments(
      primaryDetailRecord,
      listPropertyName
    );
    if (!fetchRequired) return callBack;
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getAssets({ ids });

      const returnValue = JSON.parse(JSON.stringify(data));
      returnValue.listPropertyName = listPropertyName;
      return { data: returnValue, error };
    };
  };

  const constructGroupsByIdArrayCall = (
    primaryDetailRecord,
    listPropertyName
  ) => {
    const { fetchRequired, ids, callBack } = checkArguments(
      primaryDetailRecord,
      listPropertyName
    );
    if (!fetchRequired) return callBack;

    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error
      } = await getGroups({ ids });

      const mutableCopy = JSON.parse(JSON.stringify(data));

      mutableCopy.forEach((one) => {
        if (mutableCopy.hasOwnProperty(one.type)) {
          mutableCopy[one.type].push(one);
        } else {
          mutableCopy[one.type] = [one];
        }
      });
      mutableCopy.listPropertyName = listPropertyName;

      return { data: mutableCopy, error };
    };
  };

  const constructDatazonesByIdArrayCall = (
    primaryDetailRecord,
    listPropertyName
  ) => {
    const { fetchRequired, ids, callBack } = checkArguments(
      primaryDetailRecord,
      listPropertyName
    );
    if (!fetchRequired) return callBack;

    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getDataZones({ ids });

      const returnValue = JSON.parse(JSON.stringify(data));
      returnValue.listPropertyName = listPropertyName;
      return { data: returnValue, error };
    };
  };

  const constructFeaturesByIdArrayCall = (
    primaryDetailRecord,
    listPropertyName
  ) => {
    const { fetchRequired, ids } = checkArguments(
      primaryDetailRecord,
      listPropertyName
    );

    return async () => {
      const {
        data: { data: rawCategoryList } = {
          data: []
        }
      } = await getAllFeatureCategories();

      const selectedFeatures = [];
      if (fetchRequired) {
        const {
          data: { data: rawSelectedFeatures } = {
            data: []
          }
        } = await getFeatures({
          sort: '+name',
          page: 0,
          per_page: 100,
          ids
        });

        rawSelectedFeatures.forEach((f) =>
          selectedFeatures.push(JSON.parse(JSON.stringify(f)))
        );
      }

      const {
        data: { data: pageOne } = {
          data: []
        }
      } = await getFeatures({ sort: '+name', page: 0, per_page: 100 });

      const {
        data: { data: pageTwo } = {
          data: []
        },
        error = null
      } = await getFeatures({ sort: '+name', page: 1, per_page: 100 });

      const allFeatures = pageOne.concat(pageTwo).filter((f) => f.category_id); // not taking the ones without category_id

      const categoryList = JSON.parse(JSON.stringify(rawCategoryList)).sort(
        (a, b) => {
          if (a.sort_key < b.sort_key) {
            return -1;
          }
          if (a.sort_key > b.sort_key) {
            return 1;
          }
          return 0;
        }
      );

      selectedFeatures.listPropertyName = listPropertyName;
      selectedFeatures.categoryList = categoryList;
      selectedFeatures.fullList = allFeatures;

      return { data: selectedFeatures, error };
    };
  };
  const constructServicesByIdArrayCall = (
    primaryDetailRecord,
    listPropertyName
  ) => {
    const { fetchRequired, ids, callBack } = checkArguments(
      primaryDetailRecord,
      listPropertyName
    );
    if (!fetchRequired) return callBack;
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getServices({ ids });

      const returnValue = JSON.parse(JSON.stringify(data));
      returnValue.listPropertyName = listPropertyName;
      return { data: returnValue, error };
    };
  };

  const constructPartiesByIdArrayCall = (
    primaryDetailRecord,
    listPropertyName,
    extractionPropertyName
  ) => {
    const { fetchRequired, ids, callBack } = checkArguments(
      primaryDetailRecord,
      listPropertyName,
      extractionPropertyName
    );
    if (!fetchRequired) return callBack;

    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getParties({ ids });

      const returnValue = JSON.parse(JSON.stringify(data)).map((o) => {
        if (!o.name) {
          o.name = o.first_name + ' ' + o.last_name;
        }
        return o;
      });
      returnValue.listPropertyName = listPropertyName;

      return { data: returnValue, error };
    };
  };

  const constructGetAllIndustriesCall = () => {
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getAllIndustries();

      const returnValue = JSON.parse(JSON.stringify(data));

      return { data: returnValue, error };
    };
  };

  const constructGetAllPartyTypesCall = () => {
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getPartyTypes();

      const returnValue = JSON.parse(JSON.stringify(data));

      return { data: returnValue, error };
    };
  };

  //

  const constructGetAllContractRolesCall = () => {
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getContractRoles();

      const returnValue = JSON.parse(JSON.stringify(data));

      return { data: returnValue, error };
    };
  };

  const constructGetAllBuildingsCall = () => {
    return async () => {
      const { data, error } = await fetchAllPages(getBuildings);

      const returnValue = JSON.parse(JSON.stringify(data));
      return { data: returnValue, error };
    };
  };

  //

  const constructGetAllEntityRolesCall = () => {
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getEntityRoles();

      const returnValue = JSON.parse(JSON.stringify(data));

      return { data: returnValue, error };
    };
  };

  const constructGetAllEntityTypesCall = () => {
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getEntityTypes();

      const returnValue = JSON.parse(JSON.stringify(data));

      return { data: returnValue, error };
    };
  };

  const constructGetAllServiceTypesCall = () => {
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getServiceTypes();

      const returnValue = JSON.parse(JSON.stringify(data));

      return { data: returnValue, error };
    };
  };

  const constructGetAllServiceCategoriesCall = () => {
    return async () => {
      const {
        data: { data } = {
          data: []
        },
        error = null
      } = await getServiceCategories();

      const returnValue = JSON.parse(JSON.stringify(data));

      return { data: returnValue, error };
    };
  };

  return {
    constructDatazonesByIdArrayCall,
    constructFeaturesByIdArrayCall,
    constructPartiesByIdArrayCall,
    constructGroupsByIdArrayCall,
    constructEntitiesByIdArrayCall,
    constructContractsByIdArrayCall,
    constructIndustriesByIdArrayCall,
    constructValueAsPromiseCall,
    constructTerritoryGroupsByDatazoneIdCall,
    constructServicesByIdArrayCall,
    constructAssetsByIdArrayCall,
    constructGetAllIndustriesCall,
    constructGetAllEntityTypesCall,
    constructGetAllServiceCategoriesCall,
    constructGetAllServiceTypesCall,
    constructGetAllContractRolesCall,
    constructGetAllPartyTypesCall,
    constructGetAllEntityRolesCall,
    constructGetAllAssetTypesCall,
    constructGetAllEntitiesCall,
    constructGetAllJobTitlesCall,
    constructGetAllBuildingsCall
  };
};

export default useFetchAuxiliaryLists;
